import { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import {
	Box,
	Button,
	CircularProgress,
	Container,
	FormHelperText,
	Grid,
 	IconButton,
 	InputAdornment,
	MenuItem,
	Paper,
	Skeleton,
	TextField,
	Typography,
} from '@material-ui/core';
import { DriverServiceUrl, endpoints} from 'utils/factory';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { toast } from 'sonner';

const baseDriverSchema = {
	full_name: Yup.string()
		.trim()
		.min(2, 'Full name must be at least 2 characters.')
		.max(150, 'Full name must not exceed 150 characters.')
		.required('Full name is required.'),
	email: Yup.string()
		.trim()
		.email('Please provide a valid email address.')
		.max(254, 'Email must not exceed 254 characters.')
		.required('Email is required.'),
	contact_no: Yup.string()
		.trim()
		.matches(/^[+0-9\s\-().]{7,20}$/, 'Contact number is invalid.')
		.max(20, 'Contact number must not exceed 20 characters.')
		.required('Contact number is required.'),
	street: Yup.string().trim().max(255, 'Street must not exceed 255 characters.').nullable(),
	city: Yup.string().trim().max(100, 'City must not exceed 100 characters.').nullable(),
	state: Yup.string().trim().max(100, 'State must not exceed 100 characters.').nullable(),
	postal_code: Yup.string().trim().max(20, 'Postal code must not exceed 20 characters.').nullable(),
	country: Yup.string().trim().max(100, 'Country must not exceed 100 characters.').nullable(),
	license_no: Yup.string()
		.trim()
		.max(50, 'License number must not exceed 50 characters.')
		.required('License number is required.'),
	vehicle_no: Yup.string()
		.trim()
		.max(30, 'Vehicle number must not exceed 30 characters.')
		.required('Vehicle number is required.'),
	status: Yup.number()
		.typeError('Status is required.')
		.integer('Status must be a whole number.')
		.min(0, 'Status must be between 0 and 255.')
		.max(255, 'Status must be between 0 and 255.')
		.default(1),
};

const driverRegistrationSchema = Yup.object().shape({
	...baseDriverSchema,
	password: Yup.string()
		.min(6, 'Password must be at least 6 characters.')
		.max(255, 'Password must not exceed 255 characters.')
		.required('Password is required.'),
});

const driverEditSchema = Yup.object().shape({
	...baseDriverSchema,
});

const DriverRegistration = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const driverIdParam = searchParams.get('driver');
	const isEditMode = Boolean(driverIdParam);

	const [submitMessage, setSubmitMessage] = useState('');
	const [submitError, setSubmitError] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [isLoadingDriver, setIsLoadingDriver] = useState(false);
	const [driverSno, setDriverSno] = useState(null);
	const [fetchedDriver, setFetchedDriver] = useState(null);
	const formikRef = useRef(null);

	const initialValues = useMemo(
		() => ({
			full_name: '',
			email: '',
			contact_no: '',
			password: '',
			street: '',
			city: '',
			state: '',
			postal_code: '',
			country: '',
			license_no: '',
			vehicle_no: '',
			status: 1,
			delivery_comp_id: localStorage.getItem('trans-cmp_id') || '',
		}),
		[]
	);

	const fetchDriverDetails = async () => {
		if (!driverIdParam) return;

		try {
			setIsLoadingDriver(true);
			setSubmitError('');

			const response = await axios.get(
				`${DriverServiceUrl}/${endpoints.fetchDriverDetails}?driver_id=${driverIdParam}`
			);
			const responseData = response?.data?.data;

			if (response?.data?.status === false || responseData?.status === false) {
				setSubmitError(response?.data?.message || responseData?.message || 'Failed to fetch driver details.');
				return;
			}

			const driver = Array.isArray(responseData) ? responseData[0] : responseData;

			if (driver) {
				setDriverSno(driver.sno || null);
				setFetchedDriver(driver);
			}
		} catch (error) {
			const apiMessage = error?.response?.data?.message;
			setSubmitError(apiMessage || 'Database error occurred. Please try again later.');
		} finally {
			setIsLoadingDriver(false);
		}
	};

	useEffect(() => {
		if (driverIdParam) {
			fetchDriverDetails();
		}
	}, [driverIdParam]);

	const effectiveInitialValues = useMemo(() => {
		if (fetchedDriver) {
			return {
				full_name: fetchedDriver.full_name || '',
				email: fetchedDriver.email || '',
				contact_no: fetchedDriver.contact_no || '',
				password: '',
				street: fetchedDriver.street || '',
				city: fetchedDriver.city || '',
				state: fetchedDriver.state || '',
				postal_code: fetchedDriver.postal_code || '',
				country: fetchedDriver.country || '',
				license_no: fetchedDriver.license_no || '',
				vehicle_no: fetchedDriver.vehicle_no || '',
				status: Number(fetchedDriver.status ?? 1),
				delivery_comp_id: localStorage.getItem('trans-cmp_id') || '',
			};
		}

		return initialValues;
	}, [fetchedDriver]);

	const handleSubmit = async (values, helpers) => {
		setSubmitError('');
		setSubmitMessage('');

		const payload = {
			...values,
			full_name: values.full_name.trim(),
			email: values.email.trim(),
			contact_no: values.contact_no.trim(),
			street: values.street?.trim() || null,
			city: values.city?.trim() || null,
			state: values.state?.trim() || null,
			postal_code: values.postal_code?.trim() || null,
			country: values.country?.trim() || null,
			license_no: values.license_no.trim(),
			vehicle_no: values.vehicle_no.trim(),
			status: Number(values.status ?? 1),
			delivery_comp_id: Number(localStorage.getItem('trans-userId')),
			qtype: '1',
		};

		if (isEditMode) {
			payload.driver_id = driverIdParam;
			payload.sno = driverSno;
		} else {
			payload.password_hash = values.password;
		}

		try {
			const url = isEditMode
				? `${DriverServiceUrl}/${endpoints.updateDriverDetails}`
				: `${DriverServiceUrl}/${endpoints.registerDriver}`;
			const response = await axios.post(url, payload);
			const data = response?.data;

			if (data?.status === false) {
				toast.error(data?.message || (isEditMode ? 'Driver update failed.' : 'Driver registration failed.'));
				return;
			}

			console.log(isEditMode ? 'Driver updated successfully:' : 'Driver registered successfully:', data);
			toast.success(data?.message || (isEditMode ? 'Driver updated successfully.' : 'Driver registered successfully.'));
			// Redirect to driver list or another page
			navigate('/app/driver-list');
			helpers.resetForm({ values: { ...initialValues, delivery_comp_id: values.delivery_comp_id, status: 1 } });
		} catch (error) {
			console.error(isEditMode ? 'Error during driver update:' : 'Error during driver registration:', error);
			const apiMessage = error?.response?.data?.message;
			toast.error(apiMessage || 'Database error occurred. Please try again later.');
		} finally {
			helpers.setSubmitting(false);
		}
	};

	return (
		<>
			<Helmet>
				<title>{isEditMode ? 'Edit Driver' : 'Driver Registration'}</title>
			</Helmet>
			<Box
				sx={{
					backgroundColor: 'background.default',
					minHeight: '100%',
					py: 4,
				}}
			>
				<Container maxWidth="md">
					<Paper
						elevation={2}
						style={{
							borderRadius: 16,
							overflow: 'hidden',
						}}
					>
						<Box
							sx={{
								background: 'linear-gradient(135deg, #000000 0%, #0f0f0f 40%, #1c1c1c 100%)',
								color: '#fff',
								px: 3,
								py: 3,
							}}
						>
						<Typography color="inherit" variant="h4">
							{isEditMode ? 'Edit Driver' : 'Driver Registration'}
						</Typography>
						<Typography color="inherit" variant="body2" style={{ opacity: 0.85, marginTop: 8 }}>
							{isEditMode
								? 'Update the driver details below.'
								: 'Register a new driver with contact, license, vehicle, and company details.'}
						</Typography>
						</Box>

					<Box sx={{ p: 3 }}>
					{isLoadingDriver ? (
						<Grid container spacing={2}>
							{[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) => (
								<Grid item xs={12} md={i <= 1 || i === 5 || i === 9 ? 12 : (i <= 4 || i === 8 || i === 11 ? 6 : 4)} key={i}>
									<Skeleton variant="rectangular" height={56} style={{ borderRadius: 4 }} />
								</Grid>
							))}
						</Grid>
					) : (
					<Formik
							innerRef={formikRef}
							initialValues={effectiveInitialValues}
							enableReinitialize
							validationSchema={isEditMode ? driverEditSchema : driverRegistrationSchema}
							onSubmit={handleSubmit}
						>
								{({
									errors,
									handleBlur,
									handleChange,
									handleSubmit,
									isSubmitting,
									touched,
									values,
								}) => (
									<form onSubmit={handleSubmit}>
										<Grid container spacing={2}>
											<Grid item xs={12} md={6}>
												<TextField
													error={Boolean(touched.full_name && errors.full_name)}
													fullWidth
													helperText={touched.full_name && errors.full_name}
													label="Full Name*"
													name="full_name"
													onBlur={handleBlur}
													onChange={handleChange}
													value={values.full_name}
													variant="outlined"
													disabled={isSubmitting}
												/>
											</Grid>
											<Grid item xs={12} md={6}>
												<TextField
													error={Boolean(touched.email && errors.email)}
													fullWidth
													helperText={touched.email && errors.email}
													label="Email Address*"
													name="email"
													onBlur={handleBlur}
													onChange={handleChange}
													type="email"
													value={values.email}
													variant="outlined"
													disabled={isEditMode || isSubmitting}
												/>
											</Grid>

											<Grid item xs={12} md={6}>
												<TextField
													error={Boolean(touched.contact_no && errors.contact_no)}
													fullWidth
													helperText={touched.contact_no && errors.contact_no}
													label="Contact Number*"
													name="contact_no"
													onBlur={handleBlur}
													onChange={handleChange}
													value={values.contact_no}
													variant="outlined"
													disabled={isEditMode || isSubmitting}
												/>
											</Grid>
											{!isEditMode && (
											<Grid item xs={12} md={6}>
												<TextField
													error={Boolean(touched.password && errors.password)}
													fullWidth
													helperText={touched.password && errors.password}
													label="Password*"
													name="password"
													onBlur={handleBlur}
													onChange={handleChange}
													type={showPassword ? 'text' : 'password'}
													value={values.password}
													variant="outlined"
													disabled={isSubmitting}
													InputProps={{
														endAdornment: (
															<InputAdornment position="end">
																<IconButton
																	aria-label="toggle password visibility"
																	onClick={() => setShowPassword((current) => !current)}
																	onMouseDown={(event) => event.preventDefault()}
																	edge="end"
																	disabled={isSubmitting}
																>
																	{showPassword ? <VisibilityOff /> : <Visibility />}
																</IconButton>
															</InputAdornment>
														),
													}}
												/>
											</Grid>
											)}

											<Grid item xs={12} md={6}>
												<TextField
													error={Boolean(touched.license_no && errors.license_no)}
													fullWidth
													helperText={touched.license_no && errors.license_no}
													label="License Number*"
													name="license_no"
													onBlur={handleBlur}
													onChange={handleChange}
													value={values.license_no}
													variant="outlined"
													disabled={isSubmitting}
												/>
											</Grid>
											<Grid item xs={12} md={6}>
												<TextField
													error={Boolean(touched.vehicle_no && errors.vehicle_no)}
													fullWidth
													helperText={touched.vehicle_no && errors.vehicle_no}
													label="Vehicle Number*"
													name="vehicle_no"
													onBlur={handleBlur}
													onChange={handleChange}
													value={values.vehicle_no}
													variant="outlined"
													disabled={isSubmitting}
												/>
											</Grid>

											<Grid item xs={12} md={isEditMode ? 6 : 12}>
												<TextField
													select
													error={Boolean(touched.status && errors.status)}
													fullWidth
													helperText={touched.status && errors.status}
													label="Status"
													name="status"
													onBlur={handleBlur}
													onChange={handleChange}
													value={values.status}
													variant="outlined"
													disabled={isSubmitting}
												>
													<MenuItem value={1}>Active</MenuItem>
													<MenuItem value={0}>Inactive</MenuItem>
												</TextField>
											</Grid>

											<Grid item xs={12}>
												<TextField
													error={Boolean(touched.street && errors.street)}
													fullWidth
													helperText={touched.street && errors.street}
													label="Street"
													name="street"
													onBlur={handleBlur}
													onChange={handleChange}
													value={values.street}
													variant="outlined"
													disabled={isSubmitting}
												/>
											</Grid>

											<Grid item xs={12} md={4}>
												<TextField
													error={Boolean(touched.city && errors.city)}
													fullWidth
													helperText={touched.city && errors.city}
													label="City"
													name="city"
													onBlur={handleBlur}
													onChange={handleChange}
													value={values.city}
													variant="outlined"
													disabled={isSubmitting}
												/>
											</Grid>
											<Grid item xs={12} md={4}>
												<TextField
													error={Boolean(touched.state && errors.state)}
													fullWidth
													helperText={touched.state && errors.state}
													label="State"
													name="state"
													onBlur={handleBlur}
													onChange={handleChange}
													value={values.state}
													variant="outlined"
													disabled={isSubmitting}
												/>
											</Grid>
											<Grid item xs={12} md={4}>
												<TextField
													error={Boolean(touched.postal_code && errors.postal_code)}
													fullWidth
													helperText={touched.postal_code && errors.postal_code}
													label="Postal Code"
													name="postal_code"
													onBlur={handleBlur}
													onChange={handleChange}
													value={values.postal_code}
													variant="outlined"
													disabled={isSubmitting}
												/>
											</Grid>

											<Grid item xs={12} md={6}>
												<TextField
													error={Boolean(touched.country && errors.country)}
													fullWidth
													helperText={touched.country && errors.country}
													label="Country"
													name="country"
													onBlur={handleBlur}
													onChange={handleChange}
													value={values.country}
													variant="outlined"
													disabled={isSubmitting}
												/>
											</Grid>

											<Grid item xs={12}>
												<Box
													sx={{
														alignItems: 'center',
														display: 'flex',
														justifyContent: 'space-between',
														flexWrap: 'wrap',
														gap: 2,
														mt: 1,
													}}
												>
													<Box>
														{submitError ? (
															<FormHelperText error>{submitError}</FormHelperText>
														) : null}
														{submitMessage ? (
															<Typography color="primary" variant="body2">
																{submitMessage}
															</Typography>
														) : null}
													</Box>

													<Button
														color="primary"
														disabled={isSubmitting}
														size="large"
														type="submit"
														variant="contained"
													>
														{isSubmitting ? <CircularProgress color="inherit" size={22} /> : (isEditMode ? 'Update Driver' : 'Register Driver')}
													</Button>
												</Box>
											</Grid>
										</Grid>
									</form>
								)}
							</Formik>
						)}
					</Box>
					</Paper>
				</Container>
			</Box>
		</>
	);
};

export default DriverRegistration;
